//#region IMPORT
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MAI_BASIC } from './index';
import { useSearch1 } from './MAI_BASIC_QUERY';
//#endregion

const toPeriodDays = (value) => {
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) return n;
  return 30;
};

const toArray = (v) => (Array.isArray(v) ? v : []);

/** R1: GetMaiBasicR1_MonthlyContractStats — monthlyContractStats 우선, 없으면 monthlyContractTrend */
const PickMonthlyContractTrendRaw = (first) => {
  const stats = toArray(
    first.monthlyContractStats ?? first.MonthlyContractStats
  );
  if (stats.length > 0) return stats;
  return toArray(first.monthlyContractTrend ?? first.MonthlyContractTrend);
};

/**
 * 월별 계약 회사 수·월 계약금 합 (차트용)
 * - TB_BS_COMPANY: 해당 월에 CONTRACT_START_DATE가 속하는 행만 집계
 * - CompanyCount, TotalContractAmount(=SUM 월정액)), Year·Month·YearMonth·MonthLabel
 */
const NormalizeMonthlyContractTrend = (raw) => {
  const arr = toArray(raw);
  const mapped = arr
    .map((row) => {
      const year = row.year ?? row.Year;
      const month = row.month ?? row.Month;
      const yearMonth =
        String(row.yearMonth ?? row.YearMonth ?? '').trim() ||
        (year != null && month != null
          ? `${year}-${String(month).padStart(2, '0')}`
          : '');
      const monthLabel = String(
        row.monthLabel ?? row.MonthLabel ?? yearMonth ?? ''
      ).trim();
      const sortKey = yearMonth || monthLabel;
      return {
        sortKey,
        monthLabel: monthLabel || yearMonth,
        companyCount: Number(
          row.companyCount ?? row.CompanyCount ?? row.contractCompanyCount ?? 0
        ),
        monthlyFeeTotal: Number(
          row.TotalContractAmount ??
            row.totalContractAmount ??
            row.monthlyFeeTotal ??
            row.MonthlyFeeTotal ??
            row.contractFeeSum ??
            row.ContractFeeSum ??
            0
        ),
      };
    })
    .filter((r) => r.monthLabel);
  mapped.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return mapped.map(({ sortKey, ...rest }) => ({
    ...rest,
    yearMonth: String(sortKey || '').trim(),
  }));
};

/** 당월 신규계약 월정액 합 — GetMaiBasicR1_CurrentMonthNewContractMonthlyFeeSum(CurrentMonthNewContractMonthlyFeeSum) */
const PickCurrentMonthNewContractMonthlyFeeSum = (first) => {
  const flat = Number(
    first.currentMonthNewContractMonthlyFeeSum ??
      first.CurrentMonthNewContractMonthlyFeeSum ??
      NaN
  );
  if (Number.isFinite(flat) && flat >= 0) return flat;
  return 0;
};

/** MRR·전월 대비 성장률/증감액 — GetMaiBasicR1 MonthlyTotalRevenue 우선, 없으면 mrrSummary·루트 필드 (월별통계 배열로 추정하지 않음) */
const ParseMrrFromFirst = (first) => {
  const revenueBlock = first.monthlyTotalRevenue ?? first.MonthlyTotalRevenue;
  const skipLegacyMrr =
    revenueBlock &&
    typeof revenueBlock === 'object' &&
    !Array.isArray(revenueBlock);

  let current = 0;
  let changeRate = NaN;
  let deltaAmount = NaN;

  if (skipLegacyMrr) {
    current = Number(
      revenueBlock.currentRevenue ?? revenueBlock.CurrentRevenue ?? 0
    );
    changeRate = Number(
      revenueBlock.changeRate ?? revenueBlock.ChangeRate ?? NaN
    );
    deltaAmount = Number(
      revenueBlock.revenueDeltaFromPrev ??
        revenueBlock.RevenueDeltaFromPrev ??
        NaN
    );
    const prevRev = Number(
      revenueBlock.prevRevenue ?? revenueBlock.PrevRevenue ?? NaN
    );
    if (!Number.isFinite(deltaAmount) && Number.isFinite(prevRev)) {
      deltaAmount = Number.isFinite(current) ? current - prevRev : NaN;
    }
    if (!Number.isFinite(changeRate) && Number.isFinite(prevRev)) {
      changeRate = prevRev === 0 ? 0 : ((current - prevRev) / prevRev) * 100;
    }
  }

  const summary = first.mrrSummary ?? first.MrrSummary;
  if (
    !skipLegacyMrr &&
    summary &&
    typeof summary === 'object' &&
    !Array.isArray(summary)
  ) {
    current = Number(
      summary.currentAmount ??
        summary.CurrentAmount ??
        summary.mrr ??
        summary.Mrr ??
        0
    );
    changeRate = Number(
      summary.changeRate ??
        summary.ChangeRate ??
        summary.growthRate ??
        summary.GrowthRate ??
        NaN
    );
    deltaAmount = Number(
      summary.deltaAmount ??
        summary.DeltaAmount ??
        summary.changeAmount ??
        summary.ChangeAmount ??
        NaN
    );
  }

  if (!skipLegacyMrr && !current) {
    current = Number(
      first.mrrCurrent ??
        first.MrrCurrent ??
        first.monthlyRecurringRevenue ??
        first.MRR ??
        0
    );
  }

  if (!skipLegacyMrr && !Number.isFinite(changeRate)) {
    changeRate = Number(
      first.mrrChangeRate ??
        first.mrrGrowthRate ??
        first.MrrChangeRate ??
        first.mrrMoM ??
        NaN
    );
  }

  if (!skipLegacyMrr && !Number.isFinite(changeRate)) {
    changeRate = 0;
  }

  if (!skipLegacyMrr && !Number.isFinite(deltaAmount)) {
    const prevFromSummary = Number(
      summary?.previousAmount ?? summary?.PreviousAmount ?? NaN
    );
    if (Number.isFinite(prevFromSummary)) {
      deltaAmount = current - prevFromSummary;
    } else {
      deltaAmount = 0;
    }
  }

  return {
    mrrCurrent: Number.isFinite(current) ? current : 0,
    mrrChangeRate: Number.isFinite(changeRate) ? changeRate : 0,
    mrrDeltaAmount: Number.isFinite(deltaAmount) ? deltaAmount : 0,
  };
};

/** NewSignUpCompanyCount(CurrentSignUp, ChangeRate 등) — getMaiBasicR1/R2 공통, 평탄 필드 호환 */
const ParseNewSignUpFromFirst = (first) => {
  const block = first.newSignUpCompanyCount ?? first.NewSignUpCompanyCount;
  let current = NaN;
  let changeRate = NaN;
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    current = Number(
      block.currentSignUp ?? block.CurrentSignUp ?? block.current_sign_up ?? NaN
    );
    changeRate = Number(
      block.changeRate ?? block.ChangeRate ?? block.change_rate ?? NaN
    );
  }
  if (!Number.isFinite(current)) {
    current = Number(
      first.newSignupsLast7Days ??
        first.NewSignupsLast7Days ??
        first.newCustomersLast7Days ??
        first.NewCustomersLast7Days ??
        NaN
    );
  }
  return {
    newSignupsLast7Days: Number.isFinite(current) ? current : 0,
    newSignupsChangeRate: Number.isFinite(changeRate) ? changeRate : null,
  };
};

/** 전체 총 매출 — GetMaiBasicR1_TotalRevenueSum(TotalRevenueSum) 우선, 이후 루트·요약·월별 말단 보조 */
const PickTotalOverallRevenue = (first, monthlyContractTrend) => {
  const flat = Number(
    first.totalRevenueSum ??
      first.TotalRevenueSum ??
      first.totalOverallRevenue ??
      first.TotalOverallRevenue ??
      first.totalGrossRevenue ??
      first.TotalGrossRevenue ??
      first.allActiveContractMonthlySum ??
      first.AllActiveContractMonthlySum ??
      first.totalActiveMonthlyContractAmount ??
      first.TotalActiveMonthlyContractAmount ??
      first.totalMonthlyContractRevenueSum ??
      first.TotalMonthlyContractRevenueSum ??
      NaN
  );
  if (Number.isFinite(flat) && flat >= 0) return flat;

  const block = first.totalRevenueSummary ?? first.TotalRevenueSummary;
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const v = Number(
      block.totalAmount ??
        block.TotalAmount ??
        block.amount ??
        block.Amount ??
        NaN
    );
    if (Number.isFinite(v) && v >= 0) return v;
  }

  if (monthlyContractTrend.length > 0) {
    const last = monthlyContractTrend[monthlyContractTrend.length - 1];
    const fee = Number(last?.monthlyFeeTotal ?? 0);
    if (Number.isFinite(fee) && fee >= 0) return fee;
  }
  return 0;
};

/** 올해 총 매출 — GetMaiBasicR1_YearToDateTotalRevenue(YearToDateTotalRevenue) */
const PickYearToDateTotalRevenue = (first) => {
  const flat = Number(
    first.yearToDateTotalRevenue ??
      first.YearToDateTotalRevenue ??
      first.currentYearTotalRevenue ??
      first.CurrentYearTotalRevenue ??
      NaN
  );
  if (Number.isFinite(flat) && flat >= 0) return flat;
  return 0;
};

/** 만료 임박(30일) 월정액 합 — GetMaiBasicR1_ExpiringContractCount(ExpiringContractMonthlyFeeSum) 우선 */
const PickExpiringSoonMonthlyFeeSum = (first) => {
  const fromRoot = Number(first.expiringContractMonthlyFeeSum ?? 0);
  if (Number.isFinite(fromRoot) && fromRoot >= 0) return fromRoot;

  const snapshot =
    first.expiringContractSnapshot ??
    first.ExpiringContractSnapshot ??
    first.expiringContractCountSummary ??
    first.ExpiringContractCountSummary;
  if (snapshot && typeof snapshot === 'object' && !Array.isArray(snapshot)) {
    const n = Number(
      snapshot.expiringContractMonthlyFeeSum ??
        snapshot.ExpiringContractMonthlyFeeSum ??
        NaN
    );
    if (Number.isFinite(n) && n >= 0) return n;
  }

  const block =
    first.expiringSoonRevenueSummary ??
    first.ExpiringSoonRevenueSummary ??
    first.mrrRiskSummary ??
    first.MrrRiskSummary;
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const n = Number(
      block.expiringContractMonthlyFeeSum ??
        block.ExpiringContractMonthlyFeeSum ??
        block.expiringMonthlyFeeSum ??
        block.ExpiringMonthlyFeeSum ??
        block.monthlyFeeSumWithin30Days ??
        block.MonthlyFeeSumWithin30Days ??
        block.totalMonthlyFee ??
        block.TotalMonthlyFee ??
        NaN
    );
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return Number(
    first.expiringSoonMonthlyFeeSum ??
      first.ExpiringSoonMonthlyFeeSum ??
      first.expiringContractMonthlyFeeSum ??
      first.ExpiringContractMonthlyFeeSum ??
      0
  );
};

/** 전년 동월 대비 — GetMaiBasicR1_RevenueYoYSameMonth 우선, 이후 객체·월별 트렌드 */
const PickRevenueYoYMetrics = (first, monthlyContractTrend) => {
  const packYoY = (curRaw, priRaw, crRaw) => {
    const cur = Number(curRaw);
    const pri = Number(priRaw);
    let cr = Number(crRaw);
    if (!Number.isFinite(cr) && Number.isFinite(cur) && Number.isFinite(pri)) {
      cr = pri === 0 ? 0 : ((cur - pri) / pri) * 100;
    }
    if (
      Number.isFinite(cur) ||
      Number.isFinite(pri) ||
      Number.isFinite(cr)
    ) {
      return {
        changeRate: Number.isFinite(cr) ? cr : NaN,
        currentAmount: Number.isFinite(cur) ? cur : 0,
        priorAmount: Number.isFinite(pri) ? pri : 0,
      };
    }
    return null;
  };

  const sameMonth =
    first.revenueYoYSameMonth ?? first.RevenueYoYSameMonth;
  if (sameMonth && typeof sameMonth === 'object' && !Array.isArray(sameMonth)) {
    const hit = packYoY(
      sameMonth.revenueYoYCurrentAmount ??
        sameMonth.RevenueYoYCurrentAmount,
      sameMonth.revenueYoYPriorAmount ?? sameMonth.RevenueYoYPriorAmount,
      sameMonth.revenueYoYChangeRate ?? sameMonth.RevenueYoYChangeRate
    );
    if (hit) return hit;
  }

  const flatHit = packYoY(
    first.revenueYoYCurrentAmount ?? first.RevenueYoYCurrentAmount,
    first.revenueYoYPriorAmount ?? first.RevenueYoYPriorAmount,
    first.revenueYoYChangeRate ?? first.RevenueYoYChangeRate
  );
  if (flatHit) return flatHit;

  const block =
    first.monthlyRevenueYoY ??
    first.MonthlyRevenueYoY ??
    first.revenueYoY ??
    first.RevenueYoY;
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const cur = Number(
      block.currentYearRevenue ??
        block.CurrentYearRevenue ??
        block.currentAmount ??
        block.CurrentAmount ??
        NaN
    );
    const pri = Number(
      block.priorYearRevenue ??
        block.PriorYearRevenue ??
        block.priorAmount ??
        block.PriorAmount ??
        NaN
    );
    let cr = Number(block.changeRate ?? block.ChangeRate ?? NaN);
    if (!Number.isFinite(cr) && Number.isFinite(cur) && Number.isFinite(pri)) {
      cr = pri === 0 ? 0 : ((cur - pri) / pri) * 100;
    }
    if (Number.isFinite(cr) || (Number.isFinite(cur) && Number.isFinite(pri))) {
      return {
        changeRate: Number.isFinite(cr) ? cr : NaN,
        currentAmount: Number.isFinite(cur) ? cur : 0,
        priorAmount: Number.isFinite(pri) ? pri : 0,
      };
    }
  }

  const trend = toArray(monthlyContractTrend);
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const curYm = `${y}-${String(m).padStart(2, '0')}`;
  const priYm = `${y - 1}-${String(m).padStart(2, '0')}`;

  /** MonthlyContractStats: 해당 월 계약시작 건의 TotalContractAmount 합 */
  const findFee = (targetYm) => {
    for (const r of trend) {
      const ym = String(r.yearMonth ?? '').trim();
      if (ym === targetYm) return Number(r.monthlyFeeTotal ?? 0);
    }
    for (const r of trend) {
      const lab = String(r.monthLabel ?? '');
      const mm = lab.match(/^(\d{4})년\s*(\d{1,2})월/);
      if (mm) {
        const ry = mm[1];
        const rm = String(Number(mm[2])).padStart(2, '0');
        if (`${ry}-${rm}` === targetYm) return Number(r.monthlyFeeTotal ?? 0);
      }
    }
    return NaN;
  };

  const curF = findFee(curYm);
  const priF = findFee(priYm);
  if (!Number.isFinite(curF) || !Number.isFinite(priF)) {
    return { changeRate: NaN, currentAmount: curF, priorAmount: priF };
  }
  if (priF === 0) {
    return {
      changeRate: 0,
      currentAmount: curF,
      priorAmount: priF,
    };
  }
  return {
    changeRate: ((curF - priF) / priF) * 100,
    currentAmount: curF,
    priorAmount: priF,
  };
};

/** 상위 N 고객 월정액·비중 — GetMaiBasicR1_TopCustomerRevenueShares(Rank, CompanyName, MonthlyFee, SharePercent) */
const NormalizeTopCustomerRevenueShares = (first) => {
  const raw = toArray(
    first.topCustomerRevenueShares ??
      first.TopCustomerRevenueShares ??
      first.topRevenueCustomerShares ??
      first.TopRevenueCustomerShares ??
      first.topRevenueCustomers ??
      first.TopRevenueCustomers ??
      []
  );
  return raw.map((r, i) => ({
    rank: Number(
      r.rank ?? r.Rank ?? r['Rank'] ?? i + 1
    ),
    companyName: String(
      r.companyName ??
        r.CompanyName ??
        r.coNm ??
        r.CoNm ??
        r.co_nm ??
        r.CO_NM ??
        '-'
    ).trim(),
    monthlyFee: Number(
      r.monthlyFee ??
        r.MonthlyFee ??
        r.monthlyContractFee ??
        r.MonthlyContractFee ??
        0
    ),
    sharePercent: Number(
      r.sharePercent ??
        r.SharePercent ??
        r.revenueSharePercent ??
        r.RevenueSharePercent ??
        NaN
    ),
  }));
};

/** GetMaiBasicR1_ProcActionLogLast24Hours — TotalActionCount(로그 건수) 우선, 평탄 필드 호환 */
const PickApiCallCountLast24Hours = (first) => {
  const block =
    first.procActionLogLast24Hours ?? first.ProcActionLogLast24Hours;
  if (block && typeof block === 'object' && !Array.isArray(block)) {
    const n = Number(block.totalActionCount ?? block.TotalActionCount ?? NaN);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return Number(
    first.apiCallCountLast24Hours ??
      first.ApiCallCountLast24Hours ??
      first.recent24HourApiCallCount ??
      first.Recent24HourApiCallCount ??
      first.apiRequestsLast24Hours ??
      first.ApiRequestsLast24Hours ??
      first.totalApiCalls24h ??
      first.TotalApiCalls24h ??
      first.totalApiCalls24H ??
      first.TotalApiCalls24H ??
      0
  );
};

const WEEKDAY_ORDER = ['월', '화', '수', '목', '금', '토', '일'];

const NormalizeTrafficByWeekday = (raw) => {
  const arr = toArray(raw);
  if (!arr.length) {
    return WEEKDAY_ORDER.map((w) => ({ weekdayLabel: w, userCount: 0 }));
  }
  const mapped = arr.map((row) => ({
    weekdayLabel: String(
      row.weekdayLabel ??
        row.WeekdayLabel ??
        row.dayLabel ??
        row.DayLabel ??
        row.dayName ??
        row.DayName ??
        row.label ??
        ''
    ).trim(),
    userCount: Number(
      row.activeUserCount ??
        row.ActiveUserCount ??
        row.userCount ??
        row.UserCount ??
        row.count ??
        row.Count ??
        0
    ),
  }));
  const byLabel = Object.fromEntries(
    mapped.map((r) => [r.weekdayLabel, r.userCount])
  );
  return WEEKDAY_ORDER.map((w) => ({
    weekdayLabel: w,
    userCount: byLabel[w] ?? 0,
  }));
};

const NormalizeTrafficByHour = (raw) => {
  const arr = toArray(raw);
  if (!arr.length) {
    return Array.from({ length: 24 }, (_, h) => ({
      hourLabel: `${String(h).padStart(2, '0')}시`,
      userCount: 0,
    }));
  }
  const byHour = {};
  const labelByHour = {};
  arr.forEach((row) => {
    const h = Number(
      row.hourSlot ??
        row.HourSlot ??
        row.hour ??
        row.Hour ??
        row.hourOfDay ??
        row.HourOfDay
    );
    if (!Number.isFinite(h) || h < 0 || h > 23) return;
    byHour[h] = Number(
      row.activeUserCount ??
        row.ActiveUserCount ??
        row.userCount ??
        row.UserCount ??
        row.count ??
        row.Count ??
        0
    );
    const hl = String(row.hourLabel ?? row.HourLabel ?? '').trim();
    if (hl) labelByHour[h] = hl;
  });
  return Array.from({ length: 24 }, (_, h) => ({
    hourLabel: labelByHour[h] || `${String(h).padStart(2, '0')}시`,
    userCount: byHour[h] ?? 0,
  }));
};

/** GetMaiBasicR1_DeviceTypeUsages — DEVICE_TYPE 0/1/2, UsageCount, 표시용 한글 라벨 */
const DEVICE_TYPE_ORDER = [
  { type: '0', fallbackLabel: 'TABLET', displayLabel: '태블릿' },
  { type: '1', fallbackLabel: 'MOBILE', displayLabel: '모바일' },
  { type: '2', fallbackLabel: 'WEB', displayLabel: '웹' },
];

const NormalizeDeviceTypeUsages = (raw) => {
  const arr = toArray(raw);
  const byType = {};
  arr.forEach((row) => {
    const dt = String(row.deviceType ?? row.DeviceType ?? '').trim();
    const cnt = Number(
      row.usageCount ?? row.UsageCount ?? row.count ?? row.Count ?? 0
    );
    const lab = String(row.deviceLabel ?? row.DeviceLabel ?? '').trim();
    byType[dt] = { deviceType: dt, deviceLabel: lab, usageCount: cnt };
  });
  return DEVICE_TYPE_ORDER.map((def) => {
    const found = byType[def.type];
    return {
      deviceType: def.type,
      deviceLabel: found?.deviceLabel || def.fallbackLabel,
      displayLabel: def.displayLabel,
      usageCount: Number.isFinite(found?.usageCount) ? found.usageCount : 0,
    };
  });
};

const EMPTY_DEVICE_TYPE_USAGES = NormalizeDeviceTypeUsages([]);

/** GetMaiBasicR1_TopActiveCompanies / LowUsageCompanies — CoCd, CompanyName, ActiveUserCount, ActionCount, UsageRatePercent */
const NormalizeCompanyUsageSummaries = (raw) => {
  return toArray(raw).map((r, i) => ({
    rank: Number(r.rank ?? r.Rank ?? i + 1),
    coCd: String(r.coCd ?? r.CoCd ?? '').trim(),
    companyName: String(
      r.companyName ?? r.CompanyName ?? r.coNm ?? r.CoNm ?? '-'
    ).trim(),
    contractMemberCount: Number(
      r.contractMemberCount ?? r.ContractMemberCount ?? 0
    ),
    activeUserCount: Number(
      r.activeUserCount ?? r.ActiveUserCount ?? 0
    ),
    actionCount: Number(r.actionCount ?? r.ActionCount ?? 0),
    usageRatePercent: Number(
      r.usageRatePercent ?? r.UsageRatePercent ?? 0
    ),
  }));
};

const NormalizeSessionDurationComparison = (raw) => {
  const row = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  const currentAvg = Number(
    row.currentAvgDurationSeconds ?? row.CurrentAvgDurationSeconds ?? 0
  );
  const priorAvg = Number(
    row.priorAvgDurationSeconds ?? row.PriorAvgDurationSeconds ?? 0
  );
  const changeRaw = row.changeRatePercent ?? row.ChangeRatePercent;
  const changeRate = Number(changeRaw);
  return {
    currentAvgDurationSeconds: currentAvg,
    currentSessionCount: Number(
      row.currentSessionCount ?? row.CurrentSessionCount ?? 0
    ),
    priorAvgDurationSeconds: priorAvg,
    priorSessionCount: Number(
      row.priorSessionCount ?? row.PriorSessionCount ?? 0
    ),
    changeRatePercent: Number.isFinite(changeRate) ? changeRate : null,
  };
};

const NormalizePageBounceRates = (raw) => {
  return toArray(raw).map((r, i) => ({
    rank: Number(r.rank ?? r.Rank ?? i + 1),
    menuId: String(r.menuId ?? r.MenuId ?? '').trim(),
    pageName: String(r.pageName ?? r.PageName ?? r.menuId ?? r.MenuId ?? '-').trim(),
    visitorCount: Number(r.visitorCount ?? r.VisitorCount ?? 0),
    bounceVisitorCount: Number(
      r.bounceVisitorCount ?? r.BounceVisitorCount ?? 0
    ),
    bounceRatePercent: Number(
      r.bounceRatePercent ?? r.BounceRatePercent ?? 0
    ),
  }));
};

/** GetMaiBasicR1_TopMenuFeaturesByDevice — 웹(2) 메뉴별 이용 TOP 5 */
const NormalizeTopMenuWebFeatures = (raw) => {
  return toArray(raw)
    .map((r) => ({
      deviceType: String(r.deviceType ?? r.DeviceType ?? '').trim(),
      rank: Number(r.rank ?? r.Rank ?? 0),
      menuId: String(r.menuId ?? r.MenuId ?? '').trim(),
      featureName: String(
        r.featureName ?? r.FeatureName ?? r.menuId ?? r.MenuId ?? '-'
      ).trim(),
      usageCount: Number(r.usageCount ?? r.UsageCount ?? 0),
    }))
    .filter((row) => row.deviceType === '2')
    .sort(
      (a, b) =>
        (a.rank || 99) - (b.rank || 99) || b.usageCount - a.usageCount
    )
    .slice(0, 5);
};

function MAI_BASICF() {
  const { userInfo } = useSelector((state) => state.BaseInfo);

  const [periodDays, setPeriodDays] = useState(30);
  // ✅ API 결과 컬럼명 그대로 유지
  const [dashboard, setDashboard] = useState({
    totalCompanyCount: 0,
    activeSiteCount: 0,
    expiringContractCount: 0,
    dailyActiveUsers: [],
    riskAccounts: [],
    recentAuditLogs: [],
    monthlyContractTrend: [],
    mrrCurrent: 0,
    mrrChangeRate: 0,
    mrrDeltaAmount: 0,
    currentMonthNewContractMonthlyFeeSum: 0,
    totalOverallRevenue: 0,
    yearToDateTotalRevenue: 0,
    totalContractWorkerCount: 0,
    newSignupsLast7Days: 0,
    newSignupsChangeRate: null,
    currentConcurrentUsers: 0,
    apiCallCountLast24Hours: 0,
    expiringSoonMonthlyFeeSum: 0,
    revenueYoYChangeRate: null,
    revenueYoYCurrentAmount: 0,
    revenueYoYPriorAmount: 0,
    topCustomerRevenueShares: [],
    trafficByWeekday: [],
    trafficByHour: [],
    deviceTypeUsages: EMPTY_DEVICE_TYPE_USAGES,
    topActiveCompanies: [],
    lowUsageCompanies: [],
    companyUsageRates: [],
    topMenuWeb: [],
    sessionDurationComparison: null,
    pageBounceRates: [],
    no: 0,
  });

  const safePeriodDays = useMemo(() => toPeriodDays(periodDays), [periodDays]);

  const { refetch: Search1 } = useSearch1({
    onSuccess: async (rows) => {
      const first = toArray(rows)?.[0] ?? {};
      const monthlyContractTrend = NormalizeMonthlyContractTrend(
        PickMonthlyContractTrendRaw(first)
      );
      const { mrrCurrent, mrrChangeRate, mrrDeltaAmount } =
        ParseMrrFromFirst(first);
      const { newSignupsLast7Days, newSignupsChangeRate } =
        ParseNewSignUpFromFirst(first);
      const totalOverallRevenue = PickTotalOverallRevenue(
        first,
        monthlyContractTrend
      );
      const yearToDateTotalRevenue = PickYearToDateTotalRevenue(first);
      const expiringSoonMonthlyFeeSum = PickExpiringSoonMonthlyFeeSum(first);
      const currentMonthNewContractMonthlyFeeSum =
        PickCurrentMonthNewContractMonthlyFeeSum(first);
      const revenueYoY = PickRevenueYoYMetrics(first, monthlyContractTrend);
      const topCustomerRevenueShares = NormalizeTopCustomerRevenueShares(first);

      const trafficWeekRaw =
        first.weekdayActiveUsers ??
        first.WeekdayActiveUsers ??
        first.trafficByWeekday ??
        first.TrafficByWeekday ??
        first.weeklyActiveUsers ??
        [];
      const trafficHourRaw =
        first.hourlyActiveUsers ??
        first.HourlyActiveUsers ??
        first.trafficByHour ??
        first.TrafficByHour ??
        [];
      const deviceTypeRaw =
        first.deviceTypeUsages ??
        first.DeviceTypeUsages ??
        first.deviceTypeUsageList ??
        first.DeviceTypeUsageList ??
        [];
      const topActiveRaw =
        first.topActiveCompanies ??
        first.TopActiveCompanies ??
        first.topActiveCompanyList ??
        first.TopActiveCompanyList ??
        [];
      const lowUsageRaw =
        first.lowUsageCompanies ??
        first.LowUsageCompanies ??
        first.lowUsageCompanyList ??
        first.LowUsageCompanyList ??
        [];
      const companyUsageRatesRaw =
        first.companyUsageRates ??
        first.CompanyUsageRates ??
        first.companyUsageRateList ??
        first.CompanyUsageRateList ??
        [];
      const topMenuFeaturesRaw =
        first.topMenuFeaturesByDevice ??
        first.TopMenuFeaturesByDevice ??
        first.topMenuFeatureList ??
        first.TopMenuFeatureList ??
        [];
      const topMenuWeb = NormalizeTopMenuWebFeatures(topMenuFeaturesRaw);
      const sessionDurationRaw =
        first.sessionDurationComparison ??
        first.SessionDurationComparison ??
        null;
      const pageBounceRatesRaw =
        first.pageBounceRates ??
        first.PageBounceRates ??
        first.pageBounceRateList ??
        first.PageBounceRateList ??
        [];

      setDashboard({
        totalCompanyCount: Number(
          first.totalCompanyCount ?? first.TotalCompanyCount ?? 0
        ),
        activeSiteCount: Number(
          first.activeSiteCount ?? first.ActiveSiteCount ?? 0
        ),
        expiringContractCount: Number(
          first.expiringContractCount ??
            first.ExpiringContractCount ??
            first.expiringContractSnapshot?.expiringContractCount ??
            first.ExpiringContractSnapshot?.ExpiringContractCount ??
            first.expiringContractCountSummary?.expiringContractCount ??
            first.ExpiringContractCountSummary?.ExpiringContractCount ??
            0
        ),
        dailyActiveUsers: toArray(
          first.dailyActiveUsers ?? first.DailyActiveUsers
        ),
        riskAccounts: toArray(first.riskAccounts ?? first.RiskAccounts),
        recentAuditLogs: toArray(
          first.recentAuditLogs ?? first.RecentAuditLogs
        ),
        monthlyContractTrend,
        mrrCurrent,
        mrrChangeRate,
        mrrDeltaAmount,
        currentMonthNewContractMonthlyFeeSum,
        totalOverallRevenue,
        yearToDateTotalRevenue,
        totalContractWorkerCount: Number(
          first.totalContractMemberSum ??
            first.TotalContractMemberSum ??
            first.totalContractWorkerCount ??
            first.TotalContractWorkerCount ??
            first.contractMemberTotal ??
            first.ContractMemberTotal ??
            0
        ),
        newSignupsLast7Days,
        newSignupsChangeRate,
        currentConcurrentUsers: Number(
          first.memberFcmRecentLoginUserCount ??
            first.MemberFcmRecentLoginUserCount ??
            first.currentConcurrentUsers ??
            first.CurrentConcurrentUsers ??
            first.concurrentUsers ??
            first.ConcurrentUsers ??
            0
        ),
        apiCallCountLast24Hours: PickApiCallCountLast24Hours(first),
        expiringSoonMonthlyFeeSum,
        revenueYoYChangeRate: Number.isFinite(revenueYoY.changeRate)
          ? revenueYoY.changeRate
          : null,
        revenueYoYCurrentAmount: Number.isFinite(revenueYoY.currentAmount)
          ? revenueYoY.currentAmount
          : 0,
        revenueYoYPriorAmount: Number.isFinite(revenueYoY.priorAmount)
          ? revenueYoY.priorAmount
          : 0,
        topCustomerRevenueShares,
        trafficByWeekday: NormalizeTrafficByWeekday(trafficWeekRaw),
        trafficByHour: NormalizeTrafficByHour(trafficHourRaw),
        deviceTypeUsages: NormalizeDeviceTypeUsages(deviceTypeRaw),
        topActiveCompanies: NormalizeCompanyUsageSummaries(topActiveRaw),
        lowUsageCompanies: NormalizeCompanyUsageSummaries(lowUsageRaw),
        companyUsageRates: NormalizeCompanyUsageSummaries(companyUsageRatesRaw),
        topMenuWeb,
        sessionDurationComparison: sessionDurationRaw
          ? NormalizeSessionDurationComparison(sessionDurationRaw)
          : null,
        pageBounceRates: NormalizePageBounceRates(pageBounceRatesRaw),
        no: Number(first.no ?? 0),
      });
    },
  });

  useEffect(() => {
    // userInfo가 아직 없을 때는 SP 호출이 의미 없을 수 있어, 이때는 period만 유지
    if (!userInfo?.userid) return;
    Search1();
  }, [safePeriodDays, userInfo?.userid]);

  return (
    <div className="wrap">
      <MAI_BASIC
        periodDays={safePeriodDays}
        setPeriodDays={setPeriodDays}
        dashboard={dashboard}
      />
    </div>
  );
}

export default MAI_BASICF;
